import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, FileText, Printer, Loader, Archive, Camera, History, Trash2, Calendar, User } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '@/lib/supabaseClient';

const ExportPDF = ({ outils, ateliers, emplacements = [], onClose }) => {
  const [loading, setLoading] = useState(false);
  const [selectedAtelier, setSelectedAtelier] = useState('all');
  const [selectedEmplacement, setSelectedEmplacement] = useState('all');
  const [includePhotos, setIncludePhotos] = useState(true);
  const [includeArmoirePhoto, setIncludeArmoirePhoto] = useState(true);
  const [progress, setProgress] = useState('');
  
  // États pour l'historique
  const [showHistory, setShowHistory] = useState(false);
  const [historique, setHistorique] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Liste ateliers uniques
  const ateliersUniques = useMemo(() => {
    return [...new Set(outils.map(o => o.atelier_code))].filter(Boolean);
  }, [outils]);

  // Liste emplacements en fonction de l'atelier sélectionné
  const emplacementsDisponibles = useMemo(() => {
    let outilsFiltres = outils;
    
    if (selectedAtelier !== 'all') {
      outilsFiltres = outilsFiltres.filter(o => o.atelier_code === selectedAtelier);
    }
    
    const emplacementsList = [...new Set(outilsFiltres.map(o => ({
      id: o.emplacement_id,
      nom: o.emplacement_nom,
      code: o.atelier_code
    })))];
    
    const uniqueEmplacements = emplacementsList.reduce((acc, curr) => {
      if (!acc.find(e => e.nom === curr.nom)) {
        acc.push(curr);
      }
      return acc;
    }, []);
    
    return uniqueEmplacements.sort((a, b) => a.nom.localeCompare(b.nom));
  }, [outils, selectedAtelier]);

  const handleAtelierChange = (value) => {
    setSelectedAtelier(value);
    setSelectedEmplacement('all');
  };

  // Calculer outils filtrés
  const outilsFiltres = useMemo(() => {
    let filtered = outils;
    
    if (selectedAtelier !== 'all') {
      filtered = filtered.filter(o => o.atelier_code === selectedAtelier);
    }
    
    if (selectedEmplacement !== 'all') {
      filtered = filtered.filter(o => o.emplacement_nom === selectedEmplacement);
    }
    
    return filtered;
  }, [outils, selectedAtelier, selectedEmplacement]);

  // Charger image et convertir en base64
  const loadImage = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Erreur chargement image:', url, error);
      return null;
    }
  };

  // Récupérer les données de l'emplacement sélectionné (avec photo armoire)
  const getEmplacementData = async (emplacementNom) => {
    try {
      const { data, error } = await supabase
        .from('mat_emplacements')
        .select(`
          *,
          atelier:mat_ateliers(code, nom)
        `)
        .eq('nom', emplacementNom)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur recuperation emplacement:', error);
      return null;
    }
  };

  // ========================================
  // CHARGER L'HISTORIQUE DES PDF
  // ========================================
  const loadHistorique = async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('mat_historique_pdf')
        .select(`
          *,
          emplacement:mat_emplacements(nom),
          utilisateur:profiles(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setHistorique(data || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Charger l'historique quand on ouvre le panneau
  useEffect(() => {
    if (showHistory) {
      loadHistorique();
    }
  }, [showHistory]);

  // ========================================
  // SUPPRIMER UN ENREGISTREMENT DE L'HISTORIQUE
  // ========================================
  const deleteHistoryRecord = async (id) => {
    if (!confirm('Supprimer cet enregistrement de l\'historique ?')) return;
    
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('mat_historique_pdf')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Mettre à jour la liste locale
      setHistorique(prev => prev.filter(h => h.id !== id));
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // ========================================
  // RÉGÉNÉRER UN PDF DEPUIS L'HISTORIQUE
  // ========================================
  const regeneratePDF = async (record) => {
    // Appliquer les filtres de l'enregistrement
    if (record.metadata?.atelier) {
      setSelectedAtelier(record.metadata.atelier);
    }
    if (record.metadata?.emplacement) {
      setSelectedEmplacement(record.metadata.emplacement);
    }
    if (record.metadata?.include_photos !== undefined) {
      setIncludePhotos(record.metadata.include_photos);
    }
    if (record.metadata?.include_armoire_photo !== undefined) {
      setIncludeArmoirePhoto(record.metadata.include_armoire_photo);
    }
    
    // Fermer l'historique
    setShowHistory(false);
    
    // Attendre un peu que les états se mettent à jour
    setTimeout(() => {
      generatePDF();
    }, 500);
  };

  // ========================================
  // ENREGISTRER DANS L'HISTORIQUE
  // ========================================
  const saveToHistory = async (fileName, emplacementData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const historyRecord = {
        emplacement_id: emplacementData?.id || null,
        nom_fichier: fileName,
        url_fichier: null,
        type_generation: 'manuel',
        nombre_outils: outilsFiltres.length,
        genere_par: user?.id || null,
        metadata: {
          atelier: selectedAtelier,
          emplacement: selectedEmplacement,
          include_photos: includePhotos,
          include_armoire_photo: includeArmoirePhoto,
          outils_ids: outilsFiltres.map(o => o.id)
        }
      };

      const { error } = await supabase
        .from('mat_historique_pdf')
        .insert(historyRecord);

      if (error) {
        console.error('Erreur enregistrement historique:', error);
      } else {
        console.log('Historique PDF enregistré avec succès');
      }
    } catch (error) {
      console.error('Erreur save history:', error);
    }
  };

  const generatePDF = async () => {
    setLoading(true);
    setProgress('Creation du document PDF...');

    try {
      console.log('Demarrage generation PDF...');
      
      let armoirePhotoData = null;
      let emplacementData = null;

      // Si un emplacement spécifique est sélectionné, récupérer sa photo
      if (selectedEmplacement !== 'all' && includeArmoirePhoto) {
        setProgress('Recuperation des donnees de l\'armoire...');
        emplacementData = await getEmplacementData(selectedEmplacement);
        
        if (emplacementData?.photo_armoire_url) {
          setProgress('Chargement de la photo de l\'armoire...');
          armoirePhotoData = await loadImage(emplacementData.photo_armoire_url);
        }
      }

      // Créer le document - on commence en portrait si on a une photo d'armoire
      const hasArmoirePhoto = armoirePhotoData !== null;
      
      const doc = new jsPDF({
        orientation: hasArmoirePhoto ? 'portrait' : 'landscape',
        unit: 'mm',
        format: 'a3'
      });

      console.log('Document PDF cree');

      // ========================================
      // PAGE 1 : PHOTO DE L'ARMOIRE (PORTRAIT)
      // ========================================
      if (hasArmoirePhoto && emplacementData) {
        setProgress('Ajout de la photo de l\'armoire...');
        
        const pageWidth = 297; // A3 portrait width
        const pageHeight = 420; // A3 portrait height
        
        // Titre
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('INVENTAIRE ARMOIRE', pageWidth / 2, 25, { align: 'center' });
        
        // Sous-titre
        doc.setFontSize(18);
        doc.setFont('helvetica', 'normal');
        doc.text(emplacementData.nom, pageWidth / 2, 38, { align: 'center' });
        
        // Atelier
        doc.setFontSize(14);
        doc.text('Atelier : ' + (emplacementData.atelier?.code || '') + ' - ' + (emplacementData.atelier?.nom || ''), pageWidth / 2, 50, { align: 'center' });
        
        // Date
        doc.setFontSize(12);
        doc.text('Edite le ' + new Date().toLocaleDateString('fr-FR'), pageWidth / 2, 60, { align: 'center' });
        
        // Ligne de séparation
        doc.setLineWidth(0.5);
        doc.setDrawColor(100, 100, 100);
        doc.line(20, 68, pageWidth - 20, 68);
        
        // Titre section photo (SANS EMOJI)
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('VUE D\'ENSEMBLE DE L\'ARMOIRE', pageWidth / 2, 82, { align: 'center' });
        
        // Calculer dimensions de la photo pour qu'elle soit bien centrée et grande
        const maxPhotoWidth = pageWidth - 40;
        const maxPhotoHeight = pageHeight - 130;
        
        // Charger l'image pour obtenir ses dimensions
        const img = new Image();
        img.src = armoirePhotoData;
        
        await new Promise((resolve) => {
          img.onload = () => {
            const imgRatio = img.width / img.height;
            
            let photoWidth, photoHeight;
            
            if (imgRatio > maxPhotoWidth / maxPhotoHeight) {
              photoWidth = maxPhotoWidth;
              photoHeight = maxPhotoWidth / imgRatio;
            } else {
              photoHeight = maxPhotoHeight;
              photoWidth = maxPhotoHeight * imgRatio;
            }
            
            const photoX = (pageWidth - photoWidth) / 2;
            const photoY = 90;
            
            // Cadre autour de la photo
            doc.setDrawColor(59, 130, 246);
            doc.setLineWidth(2);
            doc.rect(photoX - 3, photoY - 3, photoWidth + 6, photoHeight + 6);
            
            // Photo
            doc.addImage(armoirePhotoData, 'JPEG', photoX, photoY, photoWidth, photoHeight);
            
            // Légende (SANS EMOJI)
            doc.setFontSize(10);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100, 100, 100);
            doc.text(
              'Photo de l\'armoire "' + emplacementData.nom + '" - ' + outilsFiltres.length + ' outil(s)',
              pageWidth / 2,
              photoY + photoHeight + 15,
              { align: 'center' }
            );
            doc.setTextColor(0, 0, 0);
            
            resolve();
          };
          img.onerror = resolve;
        });
        
        // Pied de page
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Page 1 - Vue d\'ensemble', pageWidth / 2, pageHeight - 15, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        
        // Nouvelle page en paysage
        doc.addPage('a3', 'landscape');
      }

      // Dimensions page paysage
      const landscapeWidth = 420;
      const landscapeHeight = 297;

      // Titre
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('INVENTAIRE MATERIEL - CFA MONTARGIS', 20, 20);

      // Sous-titre
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let subtitle = '';
      if (selectedAtelier !== 'all') {
        subtitle += 'Atelier: ' + selectedAtelier;
      }
      if (selectedEmplacement !== 'all') {
        subtitle += subtitle ? ' | Armoire: ' + selectedEmplacement : 'Armoire: ' + selectedEmplacement;
      }
      if (subtitle) {
        doc.text(subtitle, 20, 27);
      }

      // Date
      doc.setFontSize(10);
      doc.text('Edite le ' + new Date().toLocaleDateString('fr-FR'), 20, subtitle ? 33 : 27);

      console.log('Outils filtres:', outilsFiltres.length);

      // Charger photos outils si demandé
      let outilsAvecPhotos = outilsFiltres;
      
      if (includePhotos) {
        setProgress('Chargement des photos des outils...');
        
        outilsAvecPhotos = await Promise.all(
          outilsFiltres.map(async (outil, index) => {
            setProgress('Chargement photo ' + (index + 1) + '/' + outilsFiltres.length + '...');
            
            let photoData = null;
            if (outil.photo_url) {
              photoData = await loadImage(outil.photo_url);
            }
            
            return {
              ...outil,
              photoData
            };
          })
        );
        
        console.log('Photos chargees:', outilsAvecPhotos.filter(o => o.photoData).length);
      }

      setProgress('Generation du tableau...');

      const startY = subtitle ? 40 : 35;

      if (includePhotos) {
        const tableData = outilsAvecPhotos.map(outil => {
          return [
            outil.nom || 'N/A',
            outil.reference || 'N/A',
            outil.categorie_nom || 'N/A',
            (outil.atelier_code || '') + ' - ' + (outil.emplacement_nom || ''),
            String(outil.quantite),
            outil.etat === 'complet' ? 'Complet' : 'Incomplet',
            outil.disponible ? 'Dispo' : 'Emprunte',
            ''
          ];
        });

        autoTable(doc, {
          head: [['Nom', 'Ref', 'Categorie', 'Emplacement', 'Qte', 'Etat', 'Statut', 'Photo']],
          body: tableData,
          startY: startY,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            minCellHeight: 15
          },
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 30 },
            2: { cellWidth: 40 },
            3: { cellWidth: 60 },
            4: { cellWidth: 15, halign: 'center' },
            5: { cellWidth: 25, halign: 'center' },
            6: { cellWidth: 20, halign: 'center' },
            7: { cellWidth: 25, halign: 'center' }
          },
          didDrawCell: (data) => {
            if (data.column.index === 7 && data.cell.section === 'body') {
              const outil = outilsAvecPhotos[data.row.index];
              
              if (outil && outil.photoData) {
                try {
                  const photoWidth = 20;
                  const photoHeight = 12;
                  const x = data.cell.x + (data.cell.width - photoWidth) / 2;
                  const y = data.cell.y + (data.cell.height - photoHeight) / 2;
                  
                  doc.addImage(outil.photoData, 'JPEG', x, y, photoWidth, photoHeight);
                } catch (error) {
                  console.error('Erreur ajout photo:', error);
                  doc.setFontSize(6);
                  doc.setTextColor(150);
                  doc.text('N/A', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
                  doc.setTextColor(0);
                }
              } else {
                doc.setFontSize(6);
                doc.setTextColor(150);
                doc.text('N/A', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
                doc.setTextColor(0);
              }
            }
          }
        });
        
      } else {
        const tableData = outilsFiltres.map(outil => [
          outil.nom || 'N/A',
          outil.reference || 'N/A',
          outil.categorie_nom || 'N/A',
          (outil.atelier_code || '') + ' - ' + (outil.emplacement_nom || ''),
          String(outil.quantite),
          outil.etat === 'complet' ? 'Complet' : 'Incomplet',
          outil.disponible ? 'Disponible' : 'Emprunte'
        ]);

        autoTable(doc, {
          head: [['Nom', 'Reference', 'Categorie', 'Emplacement', 'Qte', 'Etat', 'Statut']],
          body: tableData,
          startY: startY,
          styles: {
            fontSize: 9,
            cellPadding: 3
          },
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 40 },
            2: { cellWidth: 50 },
            3: { cellWidth: 70 },
            4: { cellWidth: 20, halign: 'center' },
            5: { cellWidth: 30, halign: 'center' },
            6: { cellWidth: 30, halign: 'center' }
          }
        });
      }

      console.log('Tableau genere');

      // Pied de page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        const pageSize = doc.internal.pageSize;
        const currentWidth = pageSize.getWidth();
        const currentHeight = pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(150);
        
        // Ne pas réécrire le pied de page de la première page portrait
        if (!(hasArmoirePhoto && i === 1)) {
          doc.text(
            'Page ' + i + ' sur ' + pageCount,
            currentWidth / 2,
            currentHeight - 10,
            { align: 'center' }
          );
        }
      }

      // Nom fichier
      let fileName = 'Inventaire';
      if (selectedAtelier !== 'all') {
        fileName += '_' + selectedAtelier;
      }
      if (selectedEmplacement !== 'all') {
        fileName += '_' + selectedEmplacement.replace(/\s+/g, '_');
      }
      if (includePhotos) {
        fileName += '_avec_photos';
      }
      if (hasArmoirePhoto) {
        fileName += '_vue_armoire';
      }
      fileName += '_' + new Date().toISOString().split('T')[0] + '.pdf';

      setProgress('Sauvegarde du PDF...');
      doc.save(fileName);

      // ========================================
      // ENREGISTRER DANS L'HISTORIQUE
      // ========================================
      setProgress('Enregistrement dans l\'historique...');
      await saveToHistory(fileName, emplacementData);

      console.log('PDF sauvegarde:', fileName);
      setProgress('PDF genere avec succes !');
      
      setTimeout(() => {
        alert('PDF genere avec succes !');
        onClose();
      }, 500);
      
    } catch (error) {
      console.error('Erreur generation PDF:', error);
      console.error('Stack:', error.stack);
      setProgress('Erreur : ' + error.message);
      alert('Erreur lors de la generation du PDF: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-slate-800 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-400" />
            Export PDF A3
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                showHistory 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
              }`}
            >
              <History className="w-5 h-5" />
              Historique
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {showHistory ? (
              // ========================================
              // PANNEAU HISTORIQUE
              // ========================================
              <motion.div
                key="history"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <History className="w-6 h-6 text-purple-400" />
                    Historique des exports PDF
                  </h3>
                  <button
                    onClick={loadHistorique}
                    disabled={loadingHistory}
                    className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    {loadingHistory ? <Loader className="w-4 h-4 animate-spin" /> : null}
                    Actualiser
                  </button>
                </div>

                {loadingHistory ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-purple-400" />
                  </div>
                ) : historique.length === 0 ? (
                  <div className="bg-slate-800/50 rounded-lg p-8 text-center">
                    <History className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400">Aucun historique d'export</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {historique.map((record) => (
                      <motion.div
                        key={record.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-purple-500/50 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-5 h-5 text-purple-400" />
                              <span className="text-white font-semibold text-sm truncate max-w-xs">
                                {record.nom_fichier}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(record.created_at)}
                              </div>
                              <div className="flex items-center gap-1">
                                <Archive className="w-3 h-3" />
                                {record.nombre_outils} outil(s)
                              </div>
                              {record.emplacement?.nom && (
                                <div className="col-span-2 text-purple-400">
                                  Armoire: {record.emplacement.nom}
                                </div>
                              )}
                              {record.utilisateur && (
                                <div className="col-span-2 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {record.utilisateur.full_name || record.utilisateur.email}
                                </div>
                              )}
                            </div>

                            {/* Badges options */}
                            <div className="flex gap-2 mt-2">
                              {record.metadata?.include_photos && (
                                <span className="text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded">
                                  Avec photos
                                </span>
                              )}
                              {record.metadata?.include_armoire_photo && (
                                <span className="text-xs bg-purple-900/50 text-purple-400 px-2 py-0.5 rounded">
                                  Photo armoire
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => regeneratePDF(record)}
                              className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
                              title="Regénérer ce PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteHistoryRecord(record.id)}
                              disabled={deletingId === record.id}
                              className="p-2 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white rounded-lg transition-all disabled:opacity-50"
                              title="Supprimer de l'historique"
                            >
                              {deletingId === record.id ? (
                                <Loader className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setShowHistory(false)}
                  className="w-full mt-4 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
                >
                  Retour à l'export
                </button>
              </motion.div>
            ) : (
              // ========================================
              // PANNEAU EXPORT (existant)
              // ========================================
              <motion.div
                key="export"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Printer className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold mb-1">Format d'impression</h3>
                      <p className="text-gray-300 text-sm">
                        Le PDF sera généré au format A3 avec la photo de l'armoire en portrait et le contenu en paysage.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Filtre Atelier */}
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      Atelier
                    </label>
                    <select
                      value={selectedAtelier}
                      onChange={(e) => handleAtelierChange(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Tous les ateliers</option>
                      {ateliersUniques.map(atelier => (
                        <option key={atelier} value={atelier}>
                          {atelier}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Filtre Armoire */}
                  <div>
                    <label className="flex items-center gap-2 text-white font-semibold mb-2">
                      <Archive className="w-4 h-4" />
                      Armoire / Emplacement
                    </label>
                    <select
                      value={selectedEmplacement}
                      onChange={(e) => setSelectedEmplacement(e.target.value)}
                      disabled={emplacementsDisponibles.length === 0}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="all">
                        {selectedAtelier === 'all' ? 'Toutes les armoires' : 'Toutes les armoires de cet atelier'}
                      </option>
                      {emplacementsDisponibles.map((emplacement, index) => (
                        <option key={`${emplacement.nom}-${index}`} value={emplacement.nom}>
                          {emplacement.nom}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Option Photo Armoire */}
                  {selectedEmplacement !== 'all' && (
                    <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeArmoirePhoto}
                          onChange={(e) => setIncludeArmoirePhoto(e.target.checked)}
                          className="w-5 h-5 rounded border-slate-600 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-2">
                          <Archive className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-semibold">
                            Inclure la photo de l'armoire (page portrait)
                          </span>
                        </div>
                      </label>
                      <p className="text-gray-400 text-sm mt-2 ml-8">
                        {includeArmoirePhoto ? 
                          '✅ La première page affichera la photo de l\'armoire complète en format portrait' : 
                          '❌ Seule la liste des outils sera exportée'
                        }
                      </p>
                    </div>
                  )}

                  {/* Option Photos outils */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includePhotos}
                        onChange={(e) => setIncludePhotos(e.target.checked)}
                        className="w-5 h-5 rounded border-slate-600 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-purple-400" />
                        <span className="text-white font-semibold">
                          Inclure les photos miniatures des outils
                        </span>
                      </div>
                    </label>
                    <p className="text-gray-400 text-sm mt-2 ml-8">
                      {includePhotos ? 
                        '✅ Les miniatures des outils seront affichées dans le tableau' : 
                        '❌ Seules les informations textuelles seront exportées'
                      }
                    </p>
                    {includePhotos && (
                      <p className="text-yellow-400 text-xs mt-1 ml-8">
                        Temps de generation : environ {Math.ceil(outilsFiltres.length * 0.3)} secondes
                      </p>
                    )}
                  </div>
                </div>

                {/* Progression */}
                {progress && (
                  <div className={`rounded-lg p-4 ${
                    progress.includes('succes') ? 'bg-green-900/30 border border-green-500/50' :
                    progress.includes('Erreur') ? 'bg-red-900/30 border border-red-500/50' :
                    'bg-yellow-900/30 border border-yellow-500/50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {loading && !progress.includes('succes') && !progress.includes('Erreur') && (
                        <Loader className="w-5 h-5 animate-spin text-yellow-400" />
                      )}
                      <p className={`font-semibold ${
                        progress.includes('succes') ? 'text-green-200' :
                        progress.includes('Erreur') ? 'text-red-200' :
                        'text-yellow-200'
                      }`}>
                        {progress}
                      </p>
                    </div>
                  </div>
                )}

                {/* Aperçu */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3">Apercu</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Outils a exporter:</span>
                      <span className="text-white font-bold ml-2">
                        {outilsFiltres.length}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Format:</span>
                      <span className="text-white font-bold ml-2">
                        {selectedEmplacement !== 'all' && includeArmoirePhoto ? 'A3 Portrait + Paysage' : 'A3 Paysage'}
                      </span>
                    </div>
                    {selectedAtelier !== 'all' && (
                      <div className="col-span-2">
                        <span className="text-gray-400">Atelier selectionne:</span>
                        <span className="text-purple-400 font-semibold ml-2">{selectedAtelier}</span>
                      </div>
                    )}
                    {selectedEmplacement !== 'all' && (
                      <div className="col-span-2">
                        <span className="text-gray-400">Armoire selectionnee:</span>
                        <span className="text-blue-400 font-semibold ml-2">{selectedEmplacement}</span>
                      </div>
                    )}
                    {selectedEmplacement !== 'all' && includeArmoirePhoto && (
                      <div className="col-span-2">
                        <span className="text-gray-400">Photo armoire:</span>
                        <span className="text-purple-400 font-semibold ml-2">Page 1 (Portrait)</span>
                      </div>
                    )}
                    {includePhotos && (
                      <div className="col-span-2">
                        <span className="text-gray-400">Avec photos outils:</span>
                        <span className="text-green-400 font-semibold ml-2">
                          {outilsFiltres.filter(o => o.photo_url).length} / {outilsFiltres.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {outilsFiltres.length === 0 && (
                  <div className="bg-orange-900/30 border border-orange-500/50 rounded-lg p-4">
                    <p className="text-orange-200 text-sm">
                      Aucun outil ne correspond aux filtres selectionnes
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={generatePDF}
                    disabled={loading || outilsFiltres.length === 0}
                    className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Generation...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Generer PDF
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExportPDF;